import { Suspense, lazy, useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  BRAND_IMAGE_ACCEPT, BRAND_IMAGE_MAX_BYTES, BRAND_THEMES, HEX_COLOR, brandPrimary, readableOn,
  type BrandColorsInput, type BrandImageSlot, type BrandThemeCode, type BrandingView,
} from '@pqrs/contracts';
import { Button } from '@xiltrion/ui/button';
import { CargaArchivos } from '@xiltrion/ui/bloques/carga-archivos';
import { Field as FieldUi, FieldContent, FieldLabel, FieldTitle } from '@xiltrion/ui/field';
import { Icono } from '@xiltrion/ui/icono';
import { Input } from '@xiltrion/ui/input';
import { RadioGroup, RadioGroupItem } from '@xiltrion/ui/radio-group';
import { Spinner } from '@xiltrion/ui/spinner';
import { formatBytes } from '@xiltrion/ui/use-file-upload';
import { apiDelete, apiGet, apiPut } from '@/api/client';
import { publicBrandingKey } from '@/api/branding';
import { Card, ErrorState, LoadingState } from '@/components/ui';
import { Field, confirm, notify, notifyError } from './shared';
import { SuiteManagedAlert, useSuiteManagesEntity } from './suite-managed';

const brandingKey = ['config', 'branding'] as const;
/** Asistente con pdf.js: se carga solo al abrirlo. */
const LetterheadFromDocument = lazy(() => import('./letterhead/LetterheadFromDocument'));
const useBranding = () => useQuery({ queryKey: brandingKey, queryFn: () => apiGet<BrandingView>('/branding') });

/** Contenido de un archivo en base64, sin el prefijo `data:`. */
const toBase64 = (file: File) => new Promise<string>((resolve, reject) => {
  const r = new FileReader();
  r.onload = () => resolve(String(r.result).split(',')[1] ?? '');
  r.onerror = () => reject(r.error ?? new Error('No se pudo leer el archivo'));
  r.readAsDataURL(file);
});

const SLOTS: Record<BrandImageSlot, { title: string; hint: string; alt: string; previewClass: string }> = {
  LOGO: { title: 'Logo', hint: 'Cuadrado o casi cuadrado, con fondo transparente (PNG). Aparece arriba en el portal ciudadano y en el backoffice y, si no hay franja de encabezado, junto al nombre en los documentos.', alt: 'Logo de la entidad', previewClass: 'h-20 w-auto' },
  HEADER: { title: 'Encabezado', hint: 'Franja alargada que va arriba de cada página: unos 2400 × 300 px. Suele llevar escudo, nombre y NIT.', alt: 'Encabezado del membrete', previewClass: 'w-full h-auto' },
  FOOTER: { title: 'Pie de página', hint: 'Franja alargada que va abajo de cada página: unos 2400 × 200 px. Suele llevar dirección, teléfonos y sitio web.', alt: 'Pie del membrete', previewClass: 'w-full h-auto' },
};

/**
 * Marca de la entidad (Configuración › Entidad): logo, membrete de los PDF y color de sus pantallas (portal y backoffice).
 * Con la suite conectada, el logo y el color son los de la suite y aquí solo se ven; el membrete sigue siendo de SGDEA.
 */
export function BrandingCards() {
  const q = useBranding();
  const suite = useSuiteManagesEntity();
  const qc = useQueryClient();
  const [fromDocument, setFromDocument] = useState(false);
  if (q.isLoading) return <Card title="Marca de la entidad"><LoadingState rows={3} /></Card>;
  if (q.error || !q.data) return <Card title="Marca de la entidad"><ErrorState error={q.error} onRetry={() => void q.refetch()} /></Card>;
  const b = q.data;
  return (
    <>
      <Card title={<span className="flex items-center gap-2"><Icono nombre="mdi:image-outline" aria-hidden /> Logo</span>}>
        {suite ? <SuiteLogo view={b} url={suite.url} /> : <ImageSlot slot="LOGO" view={b} />}
      </Card>
      <Card title={<span className="flex items-center gap-2"><Icono nombre="mdi:file-document-edit-outline" aria-hidden /> Membrete de los documentos</span>}
        actions={<div className="flex flex-wrap gap-2">
          <Button size="sm" variant="outline" onClick={() => setFromDocument(true)}><Icono nombre="mdi:file-find-outline" /> Desde un documento</Button>
          <Button asChild size="sm" variant="outline"><a href="/api/v1/branding/preview.pdf" target="_blank" rel="noreferrer"><Icono nombre="mdi:file-pdf-box" /> Vista previa</a></Button>
        </div>}>
        <p className="text-sm text-muted-foreground m-0 mb-4">
          Va en los PDF que emite la entidad: la constancia de radicación, las comunicaciones al ciudadano y las respuestas. Los documentos que ya están en un expediente no cambian.
          {' '}Si no tienes las imágenes del encabezado y del pie, usa «Desde un documento» con una carta o plantilla de la entidad.
        </p>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <ImageSlot slot="HEADER" view={b} />
          <ImageSlot slot="FOOTER" view={b} />
        </div>
      </Card>
      <ColorsCard view={b} suiteUrl={suite ? suite.url ?? '' : null} />
      {fromDocument && (
        <Suspense fallback={null}>
          <LetterheadFromDocument onHide={() => setFromDocument(false)}
            onSaved={(data) => { qc.setQueryData(brandingKey, data); setFromDocument(false); notify('success', 'Membrete guardado', 'Los documentos nuevos saldrán con este encabezado y pie.'); }} />
        </Suspense>
      )}
    </>
  );
}

function ImageSlot({ slot, view }: { slot: BrandImageSlot; view: BrandingView }) {
  const qc = useQueryClient();
  const meta = SLOTS[slot];
  const current = view.images[slot];
  const [files, setFiles] = useState<File[]>([]);
  // El logo también lo muestra el menú del backoffice: se refresca al cambiarlo.
  const done = (data: BrandingView) => { qc.setQueryData(brandingKey, data); if (slot === 'LOGO') void qc.invalidateQueries({ queryKey: publicBrandingKey }); setFiles([]); };
  const upload = useMutation({
    mutationFn: async (file: File) => apiPut<BrandingView>(`/branding/images/${slot}`, { fileName: file.name, base64: await toBase64(file) }),
    onSuccess: (data) => { done(data); notify('success', `${meta.title} guardado`); },
    onError: (e) => { setFiles([]); notifyError(e); },
  });
  const remove = useMutation({ mutationFn: () => apiDelete<BrandingView>(`/branding/images/${slot}`), onSuccess: (data) => { done(data); notify('success', `${meta.title} quitado`); }, onError: notifyError });
  const pick = (list: File[]) => { const f = list.at(-1); setFiles(f ? [f] : []); if (f) upload.mutate(f); };
  const ask = async () => { if (await confirm({ header: `Quitar ${meta.title.toLowerCase()}`, message: 'Los documentos nuevos saldrán sin esta imagen. Los que ya están en los expedientes no cambian.', acceptLabel: 'Quitar', danger: true })) remove.mutate(); };
  return (
    <Field label={meta.title} hint={meta.hint}>
      {current && (
        <div className="flex flex-col gap-2 rounded-md border border-border bg-muted p-3">
          <img src={current.dataUrl} alt={meta.alt} className={`${meta.previewClass} max-w-full self-start object-contain`} />
          <div className="flex items-center justify-between gap-2 text-xs text-muted-foreground">
            <span className="tnum truncate">{current.fileName} · {current.width} × {current.height} px · {formatBytes(current.sizeBytes)}</span>
            <Button size="sm" variant="ghost" className="text-destructive hover:text-destructive" disabled={remove.isPending} onClick={() => void ask()}>{remove.isPending ? <Spinner /> : <Icono nombre="mdi:trash-can-outline" />} Quitar</Button>
          </div>
        </div>
      )}
      <CargaArchivos archivos={files} onArchivosChange={pick} accept={BRAND_IMAGE_ACCEPT} maxTamano={BRAND_IMAGE_MAX_BYTES[slot]} maxArchivos={1} deshabilitado={upload.isPending}
        compacto={!!current} titulo={upload.isPending ? 'Guardando…' : 'Arrastra aquí una imagen'} textoElegir="elígela"
        textoAyuda={`PNG o JPG, hasta ${formatBytes(BRAND_IMAGE_MAX_BYTES[slot])}`} aria-label={`Cargar ${meta.title.toLowerCase()}`} />
    </Field>
  );
}

/** El logo de la suite, solo para ver. */
function SuiteLogo({ view, url }: { view: BrandingView; url: string | null }) {
  const current = view.images.LOGO;
  return (
    <div className="flex flex-col gap-3">
      <SuiteManagedAlert url={url}>El logo de la entidad lo cambia su administrador en la suite Datalogic (Perfil y marca) y vale para todas sus apps. En los PDF entra si es PNG o JPG.</SuiteManagedAlert>
      {current
        ? <div className="rounded-md border border-border bg-muted p-3"><img src={current.dataUrl} alt={SLOTS.LOGO.alt} className={`${SLOTS.LOGO.previewClass} max-w-full object-contain`} /></div>
        : <p className="text-sm text-muted-foreground m-0">La entidad aún no tiene logo en la suite.</p>}
    </div>
  );
}

/** `suiteUrl`: con la suite conectada (cadena vacía si no hay enlace), los colores solo se ven. */
function ColorsCard({ view, suiteUrl }: { view: BrandingView; suiteUrl: string | null }) {
  const locked = suiteUrl !== null;
  const qc = useQueryClient();
  const [f, setF] = useState<{ theme: BrandThemeCode; customColor: string }>({ theme: view.theme, customColor: view.customColor ?? '#1d4ed8' });
  useEffect(() => { setF({ theme: view.theme, customColor: view.customColor ?? '#1d4ed8' }); }, [view.theme, view.customColor]);
  const valid = f.theme !== 'CUSTOM' || HEX_COLOR.test(f.customColor);
  const dirty = f.theme !== view.theme || (f.theme === 'CUSTOM' && f.customColor.toLowerCase() !== view.customColor);
  const save = useMutation({
    mutationFn: () => apiPut<BrandingView>('/branding/colors', { theme: f.theme, customColor: f.theme === 'CUSTOM' ? f.customColor : null } satisfies BrandColorsInput),
    onSuccess: (data) => { qc.setQueryData(brandingKey, data); notify('success', 'Colores de la entidad guardados'); },
    onError: notifyError,
  });
  const options = [...BRAND_THEMES.map((t) => ({ code: t.code as BrandThemeCode, label: t.label, primary: t.primary })), { code: 'CUSTOM' as const, label: 'Personalizado', primary: HEX_COLOR.test(f.customColor) ? f.customColor : null }];
  const primary = brandPrimary(f.theme, f.customColor);
  return (
    <Card title={<span className="flex items-center gap-2"><Icono nombre="mdi:palette-outline" aria-hidden /> Colores de la entidad</span>}
      actions={!locked && <Button size="sm" disabled={!dirty || !valid || save.isPending} onClick={() => save.mutate()}>{save.isPending ? <Spinner /> : <Icono nombre="mdi:check" />} Guardar</Button>}>
      {locked
        ? <div className="mb-4"><SuiteManagedAlert url={suiteUrl || null}>El color de la entidad lo cambia su administrador en la suite Datalogic (Perfil y marca): es el del portal ciudadano y el de esta aplicación.</SuiteManagedAlert></div>
        : <p className="text-sm text-muted-foreground m-0 mb-4">El color principal de las pantallas de la entidad: el portal donde los ciudadanos radican y consultan y esta misma aplicación. Se aplica al guardar.</p>}
      <RadioGroup disabled={locked} value={f.theme} onValueChange={(v) => setF({ ...f, theme: v as BrandThemeCode })} className="grid-cols-2 md:grid-cols-4 gap-2" aria-label="Tema de la entidad">
        {options.map((o) => (
          <FieldLabel key={o.code} htmlFor={`brand-theme-${o.code}`} className="cursor-pointer">
            <FieldUi orientation="horizontal">
              <FieldContent className="flex-row items-center gap-2">
                {/* El color es un dato de la entidad, no un token del sistema de diseño: va en línea. El de Xiltrion no sigue al de la entidad. */}
                <span aria-hidden className={`size-5 shrink-0 rounded-full border border-border ${o.primary ? '' : 'bg-(--primary-xiltrion)'}`} style={o.primary ? { backgroundColor: o.primary } : undefined} />
                <FieldTitle>{o.label}</FieldTitle>
              </FieldContent>
              <RadioGroupItem id={`brand-theme-${o.code}`} value={o.code} />
            </FieldUi>
          </FieldLabel>
        ))}
      </RadioGroup>
      {f.theme === 'CUSTOM' && !locked && (
        <div className="mt-4 flex items-end gap-3">
          <Field label="Color" htmlFor="brand-color-picker">
            <Input id="brand-color-picker" type="color" className="h-9 w-16 p-1 cursor-pointer" value={HEX_COLOR.test(f.customColor) ? f.customColor : '#000000'} onChange={(e) => setF({ ...f, customColor: e.target.value })} />
          </Field>
          <Field label="Código hexadecimal" htmlFor="brand-color-hex" hint={valid ? undefined : 'Formato #RRGGBB'}>
            <Input id="brand-color-hex" className="tnum w-32 uppercase" value={f.customColor} maxLength={7} aria-invalid={!valid} onChange={(e) => setF({ ...f, customColor: e.target.value.startsWith('#') ? e.target.value : `#${e.target.value}` })} />
          </Field>
        </div>
      )}
      {primary && (
        <div className="mt-4 flex items-center gap-3 text-sm text-muted-foreground">
          Así se ve un botón:
          <span className="inline-flex items-center rounded-md px-3 py-1.5 text-sm font-medium" style={{ backgroundColor: primary, color: readableOn(primary) }}>Radicar solicitud</span>
        </div>
      )}
    </Card>
  );
}

